// Browser-side OIDC PKCE helper.
//
// Backend handles the heavy lifting: /auth/login starts the redirect flow,
// /auth/callback exchanges the code and sets the session cookie. This module
// is a thin client around those endpoints + a useSession hook for components.
//
// We deliberately do NOT store tokens in localStorage. The session lives in
// a HttpOnly cookie set by the backend; SPA reads /v1/me to get the user.

import { useEffect, useState } from 'react';

// Production: Traefik routes PathPrefix(`/v1`) directly to the api binary.
// Dev: Vite proxy maps /v1 → :8080 (configured in vite.config.js).
// VITE_API_BASE override exists for unusual deploys (e.g. /api StripPrefix).
const API_BASE = import.meta.env.VITE_API_BASE ?? '';

// startLogin redirects the browser to /auth/login on the backend, which in
// turn redirects to ZITADEL. Optional idp_hint forwards to ZITADEL so the
// user lands on a specific identity provider screen (e.g. google).
export function startLogin(idpHint) {
  const u = new URL('/auth/login', window.location.origin);
  if (idpHint) u.searchParams.set('idp_hint', idpHint);
  window.location.assign(u.toString());
}

// logout calls /auth/logout which clears the session cookie + redirects to
// ZITADEL end_session, which redirects back to /.
export function logout() {
  window.location.assign('/auth/logout');
}

// fetchMe asks the backend for the current user; returns null if not logged in.
export async function fetchMe() {
  const res = await fetch(`${API_BASE}/v1/workspace`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// useSession returns { workspaces, loading, error }. workspaces is null when
// the user is unauthenticated; an array (possibly empty) when logged in.
export function useSession() {
  const [state, setState] = useState({ workspaces: null, loading: true, error: null });
  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((data) => {
        if (cancelled) return;
        if (data === null) {
          setState({ workspaces: null, loading: false, error: null });
        } else {
          setState({ workspaces: data.workspaces || [], loading: false, error: null });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ workspaces: null, loading: false, error: err });
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return state;
}
